import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import { displayTraceId, newTraceparent, traceIdOf } from '@core/interceptors/trace-context';
import { fileDownload } from '@core/misc/misc.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { dispatch } from '@ngxs/store';
import { reservationRefresh } from '@pages/reservations-page/state/reservations.action';
import { ReservationsApiService } from '@pages/reservations-page/services/reservations-api.service';
import {
  INSURANCE_ISSUE_IN_PROGRESS,
  INSURANCE_PROVIDER_REFUSED,
  INSURANCE_REGISTRATION_PENDING,
  INSURANCE_SERVICE_UNAVAILABLE,
  INSURANCE_TOURIST_DATA_INVALID,
  InsuranceAwareDocument
} from '@pages/reservations-page/reservation-insurance.types';

/** The seven states of the line (docs features/insurance-service/qa.md "Stage 2 - website cabinet"), plus the generic coded refusal of an unknown code. */
export type InsuranceRowState =
  'idle' | 'waiting' | 'in-progress' | 'issued' | 'operator' | 'refused' | 'pending' | 'unavailable' | 'failed';

type Tone = 'ok' | 'bad' | 'warn' | 'muted';
type RowAction = 'create' | 'busy' | 'retry' | 'download';

interface RowOverride {
  state: InsuranceRowState;
  code?: string;
  traceId?: string;
  reason?: string;
}

interface RowView {
  status: string;
  tone: Tone;
  message: string | null;
  nothingIssued: boolean;
  code: string | null;
  showTrace: boolean;
  action: RowAction;
}

const GENERIC_ERROR_TEXT = "It seems that something has gone wrong. Don't worry, our support team is ready to help you";

/** `yyyy-MM-dd[...]` -> `dd.mm.yyyy` (C24) without a time-zone shift; anything else is not shown. */
export const formatIssueDate = (value: string | null | undefined): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : null;
};

/** The tourist id from a document URL `...&documentId=<touristId>.<policyId>&...` - used when the API block carries none. */
const touristIdFromUrl = (url: string | null | undefined): string | null => {
  const match = /[?&]documentId=([0-9a-fA-F-]{36})/.exec(url ?? '');
  return match ? match[1] : null;
};

/**
 * One tourist's insurance line in the cabinet's **Documents** popup, drawn when the API sends the `insurance` block (engine `InsuranceService`).
 *
 * QA doc: docs features/insurance-service/qa.md ("Stage 2 - website cabinet", R-52, C17, C23). Rules enforced here: ONE agreement per tourist - the
 * line offers Create insurance only for a Draft tourist, an issued or operator-issued policy only downloads (R-37 / R-52); a refusal is drawn by its
 * `errorCode` with the Trace ID this browser sent (copyable), never as a stale or configured copy (C17); every text is a translation key (C21);
 * the issue day is dd.mm.yyyy (C24); every control carries `cabinet-insurance-<what>-<touristId>` (C16).
 */
@Component({
  selector: 'app-reservation-insurance-row',
  imports: [TranslatePipe, MatButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reservation-insurance-row.component.html',
  styleUrl: './reservation-insurance-row.component.scss'
})
export class ReservationInsuranceRowComponent {
  readonly #api = inject(ReservationsApiService);
  readonly #clipboard = inject(Clipboard);
  readonly #refresh = dispatch(reservationRefresh);

  /** The `Insurance` document of the tourist (carries the `insurance` block). */
  public readonly document = input.required<InsuranceAwareDocument>();
  public readonly reservationId = input<string | null | undefined>();
  public readonly reservationNumber = input<string | null | undefined>();

  /** What the last click produced; `null` = the line shows what the API said when the popup opened. */
  readonly #override = signal<RowOverride | null>(null);
  readonly #downloading = signal(false);
  readonly copied = signal(false);

  public readonly touristId = computed(
    () =>
      this.document().insurance?.touristId ?? touristIdFromUrl(this.document().url) ?? String(this.document().url ?? '')
  );

  public readonly description = computed(() => (this.document().descriptions ?? []).filter(Boolean));

  public readonly state = computed<InsuranceRowState>(() => {
    const override = this.#override();
    if (override) return override.state;
    const insurance = this.document().insurance;
    if (insurance?.status === 'Confirmed') return insurance.issuedByOperator ? 'operator' : 'issued';
    if (insurance?.status === 'InProcess') return 'in-progress';
    return 'idle';
  });

  public readonly traceId = computed(() => this.#override()?.traceId ?? null);
  public readonly policyNumber = computed(() => this.document().insurance?.policyNumber ?? null);
  public readonly issueDate = computed(() => formatIssueDate(this.document().insurance?.issueDate));

  /** The text for each state, key = English text (C21). */
  public readonly view = computed<RowView>(() => {
    const override = this.#override();
    const code = override?.code ?? null;
    switch (this.state()) {
      case 'waiting':
        return {
          status: 'Issuing...',
          tone: 'warn',
          message: 'The insurance is being issued - this can take up to a minute. Keep this window open.',
          nothingIssued: false,
          code: null,
          showTrace: true,
          action: 'busy'
        };
      case 'in-progress':
        return {
          status: 'Issuing...',
          tone: 'warn',
          message: 'The policy is being issued right now - wait a moment, the download appears here.',
          nothingIssued: false,
          code,
          showTrace: true,
          action: 'retry'
        };
      case 'issued':
        return {
          status: 'Issued',
          tone: 'ok',
          message: null,
          nothingIssued: false,
          code: null,
          showTrace: false,
          action: 'download'
        };
      case 'operator':
        return {
          status: 'Issued by your operator',
          tone: 'ok',
          message: null,
          nothingIssued: false,
          code: null,
          showTrace: false,
          action: 'download'
        };
      case 'refused':
        return {
          status: 'Not issued',
          tone: 'bad',
          message: null,
          nothingIssued: true,
          code,
          showTrace: true,
          action: 'retry'
        };
      case 'pending':
        return {
          status: 'Still being registered',
          tone: 'warn',
          message: 'The insurance is still being registered - try again in a minute.',
          nothingIssued: false,
          code,
          showTrace: true,
          action: 'retry'
        };
      case 'unavailable':
        return {
          status: 'Service not available',
          tone: 'bad',
          message:
            'Insurance service is not available - nothing was changed. Try again in a few minutes; if it persists, send the Trace ID to support.',
          nothingIssued: false,
          code,
          showTrace: true,
          action: 'retry'
        };
      case 'failed':
        return {
          status: 'Not issued',
          tone: 'bad',
          message: GENERIC_ERROR_TEXT,
          nothingIssued: false,
          code,
          showTrace: true,
          action: 'retry'
        };
      default:
        return {
          status: 'Not issued yet',
          tone: 'muted',
          message: null,
          nothingIssued: false,
          code: null,
          showTrace: false,
          action: 'create'
        };
    }
  });

  /** The insurer's own words for a refusal (`INSURANCE_PROVIDER_REFUSED` / `INSURANCE_TOURIST_DATA_INVALID`). */
  public readonly reason = computed(() => this.#override()?.reason ?? null);
  public readonly busy = computed(() => this.state() === 'waiting' || this.#downloading());

  /** Create insurance / Download policy: one POST of the document URL, the same call the classic Download made. */
  public onAct(): void {
    const url = this.document().url;
    if (!url || this.busy()) return;
    const issuing = this.state() === 'idle';
    const traceparent = newTraceparent();
    const traceId = displayTraceId(traceIdOf(traceparent)) ?? undefined;
    if (issuing) this.#override.set({ state: 'waiting', traceId });
    else this.#downloading.set(true);

    this.#api.getDocument(url, traceparent).subscribe({
      next: (response) => {
        const blob = response.body as Blob;
        fileDownload(blob, `${this.reservationNumber() ?? ''} Insurance`.trim());
        this.#downloading.set(false);
        if (issuing) {
          this.#override.set({ state: 'issued' });
          const id = this.reservationId();
          if (id) this.#refresh(id);
        }
      },
      error: (error: ApiErrorResponse) => {
        this.#downloading.set(false);
        this.#override.set(this.#overrideFor(error, traceId));
      }
    });
  }

  /** Try again: back to what the API said when the popup opened (Create insurance for a Draft tourist). */
  public onRetry(): void {
    this.#override.set(null);
  }

  public onCopyTrace(): void {
    const id = this.traceId();
    if (id) this.copied.set(this.#clipboard.copy(id));
  }

  #overrideFor(error: ApiErrorResponse, sentTraceId: string | undefined): RowOverride {
    const { code, text } = this.#codeOf(error);
    const traceId = displayTraceId(error.activityId) ?? sentTraceId;
    const base = { code, traceId };
    switch (code) {
      case INSURANCE_ISSUE_IN_PROGRESS:
        return { ...base, state: 'in-progress' };
      case INSURANCE_PROVIDER_REFUSED:
      case INSURANCE_TOURIST_DATA_INVALID:
        return { ...base, state: 'refused', reason: text ?? this.#reasonOf(error) };
      case INSURANCE_REGISTRATION_PENDING:
        return { ...base, state: 'pending' };
      case INSURANCE_SERVICE_UNAVAILABLE:
        return { ...base, state: 'unavailable' };
      default:
        // C17: a plain 503 is the service being down; anything else is shown as a coded generic refusal - never a guess about what was changed.
        return { ...base, state: error.status === 503 ? 'unavailable' : 'failed' };
    }
  }

  /**
   * The code of a refusal. reservations-api answers HTTP 400 with the text `CODE: English text` (the `x-nugios-error` convention, qa.md R-52b), so
   * the interceptor's `errorCode` may be a generic one - the code is then read from the front of the text, and what follows it is the insurer's reason.
   */
  #codeOf(error: ApiErrorResponse): { code: string | undefined; text: string | undefined } {
    for (const message of [error.sysErrorMessage, error.displayErrorMessage]) {
      const text = (message ?? '').trim();
      const colon = text.indexOf(':');
      const code = colon > 0 ? text.slice(0, colon) : '';
      if (code.startsWith('INSURANCE_') && /^[A-Z_]+$/.test(code)) {
        return { code, text: text.slice(colon + 1).trim() || undefined };
      }
    }
    return { code: error.errorCode, text: undefined };
  }

  #reasonOf(error: ApiErrorResponse): string | undefined {
    const candidates = [error.displayErrorMessage, error.sysErrorMessage];
    return (
      candidates.find((text) => !!text && text !== error.errorCode && !text.startsWith('Http failure response')) ??
      undefined
    );
  }
}
